import React, { useState, useEffect, useRef, forwardRef } from 'react';
import axios from 'axios';
import { Table, Button, Offcanvas, Form, Row, Col, Modal, Dropdown } from 'react-bootstrap';
import Swal from 'sweetalert2';
import LicenseInfoDetails from './LicenseInfoDetails';
import ReactPaginate from 'react-paginate';
import dayjs from 'dayjs';
import { useLocation } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const Toast = Swal.mixin({
  toast: true,
  position: 'top-end',
  showConfirmButton: false,
  timer: 3000,
  timerProgressBar: true,
  didOpen: (toast) => {
    toast.onmouseenter = Swal.stopTimer;
    toast.onmouseleave = Swal.resumeTimer;
  }
});

const backendUrl = process.env.REACT_APP_BACKEND_URL;
const getHeaders = () => {
  const userInfo = JSON.parse(localStorage.getItem('user-info') || '{}');
  const token = userInfo?.token;
  const origin = localStorage.getItem('user-origin');
  return {
    Authorization: `Bearer ${token}`,
    'Tenant-Origin': origin
  };
};


const CustomToggle = forwardRef(({ children, onClick }, ref) => (
  <button
    ref={ref}
    type="button"
    onClick={(e) => {
      e.preventDefault();
      onClick(e);
    }}
    className="btn rounded border d-flex align-items-center justify-content-center shadow-sm bg-white text-muted px-3"
    style={{ height: '38px' }}
    title="Filter by Expiry"
  >
    <i className="mdi mdi-tune font-size-18 me-2"></i> Filter
  </button>
));

const LicenseInfoList = ({ setupId, isEmbedded, closeParentOffcanvas, onChildModalToggle, activeExpiryFilter, autoOpenLicenseId }) => {
  const [licenseInfos, setLicenseInfos] = useState([]);
  const [setupInfos, setSetupInfos] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);
  const [modalStartDate, setModalStartDate] = useState(null);
  const [modalValidityDate, setModalValidityDate] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [expiryFilter, setExpiryFilter] = useState(activeExpiryFilter || '');
  
  useEffect(() => {
    if (activeExpiryFilter !== undefined) {
      setExpiryFilter(activeExpiryFilter);
    }
  }, [activeExpiryFilter]);

  const handleExpiryFilterChange = (e) => {
    setExpiryFilter(e.target.value);
  };
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [extendData, setExtendData] = useState({ type: 'days', value: 1 });
  
  const debounceTimeoutRef = useRef(null);

  useEffect(() => {
    if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
        fetchLicenseInfos(1, searchQuery, expiryFilter);
    }, searchQuery ? 1500 : 0);

    return () => {
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }
    };
  }, [searchQuery, setupId, activeExpiryFilter, expiryFilter]);

  useEffect(() => {
    fetchSetupInfos();
  }, []);

  const handleExtendSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.patch(`${backendUrl}/api/v360-license-info/${currentRecord.id}`, {
        extension_type: extendData.type,
        extension_days: parseInt(extendData.value, 10)
      }, { headers: getHeaders() });
      Toast.fire({ icon: 'success', title: 'License extended successfully' });
      setShowExtendModal(false);
      fetchLicenseInfos(currentPage + 1);
    } catch (error) {
      console.error('Error extending license:', error);
      Toast.fire({ icon: 'error', title: 'Failed to extend license' });
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const fetchLicenseInfos = async (page = 1, search = searchQuery, filter = expiryFilter) => {
    try {
      const params = { page, search };
      if (setupId) {
        params.setup_id = setupId;
      }
      if (filter) {
        params.expiry_filter = filter;
      }
      const response = await axios.get(`${backendUrl}/api/v360-license-info`, { params, headers: getHeaders() });
      const resData = response.data;
      if (resData && resData.data) {
        setLicenseInfos(resData.data);
        setCurrentPage((resData.current_page || 1) - 1);
        setPageCount(resData.last_page || 1);
      } else {
        // Fallback for unexpected format
        setLicenseInfos(Array.isArray(resData) ? resData : []);
      }
    } catch (error) {
      console.error('Error fetching license infos', error);
      Toast.fire({ icon: 'error', title: 'Failed to load license infos' });
    }
  };

  useEffect(() => {
    if (onChildModalToggle) {
      onChildModalToggle(showExtendModal || showDetailsModal);
    }
  }, [showExtendModal, showDetailsModal, onChildModalToggle]);

  const fetchSetupInfos = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/v360-setup-info`, { headers: getHeaders() });
      setSetupInfos(response.data);
    } catch (error) {
      console.error('Error fetching setup infos', error);
    }
  };

  const handleShowModal = (record = null) => {
    setCurrentRecord(record);
    if (record) {
      setModalStartDate(record.start_date ? new Date(record.start_date) : null);
      const activeValidity = record.extended_validity || record.validity;
      setModalValidityDate(activeValidity ? new Date(activeValidity) : null);
    } else {
      setModalStartDate(null);
      setModalValidityDate(null);
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setCurrentRecord(null);
    setModalStartDate(null);
    setModalValidityDate(null);
    setShowModal(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    const numericFields = [
      'setup_id', 'tenant_id', 'user_login', 'user_create', 
      'role_s_admin', 'role_admin', 'role_supervisor', 'role_teamlead', 'role_analyst', 
      'role_cagent', 'role_vagent', 'role_tagent', 'role_agent', 'mobile_app', 
      'module_email', 'module_voicemail', 'module_chat', 'module_interaction', 
      'module_voice', 'module_api', 'role_agent_plus', 'module_video', 'wallboard', 
      'gen_ai', 'toolbar_app', 'ai_assist', 'ai_assist_pro',
      'module_ticket', 'camp_voice', 'camp_waba', 'camp_sms', 'camp_email'
    ];
    
    for (let field of numericFields) {
      if (data[field] !== undefined) {
        if (data[field] === "") {
          data[field] = 0;
        } else {
          const parsed = Number(data[field]);
          if (isNaN(parsed)) {
            Toast.fire({ icon: 'error', title: `${field.replace('_', ' ').toUpperCase()} must be a valid number` });
            return;
          }
          data[field] = parsed;
        }
      }
    }

    const formatDateToIso = (dateStr) => {
      if (!dateStr) return null;
      const str = String(dateStr).trim();
      const parts = str.split('-');
      if (parts.length === 3 && parts[0].length === 2 && parts[2].length === 4) {
        return `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
      return str;
    };

    data.start_date = formatDateToIso(data.start_date);
    data.validity = formatDateToIso(data.validity);

    data.ip_check = formData.get('ip_check') === 'on';
    data.ips = data.ips ? String(data.ips).split(',').map(ip => ip.trim()).filter(Boolean) : [];

    try {
      if (currentRecord && currentRecord.id) {
        await axios.patch(`${backendUrl}/api/v360-license-info/${currentRecord.id}`, data, { headers: getHeaders() });
        Toast.fire({ icon: 'success', title: 'Record updated successfully' });
      } else {
        await axios.post(`${backendUrl}/api/v360-license-info`, data, { headers: getHeaders() });
        Toast.fire({ icon: 'success', title: 'Record created successfully' });
      }
      handleCloseModal();
      fetchLicenseInfos(currentPage + 1);
    } catch (error) {
      console.error('Error saving record', error);
      Toast.fire({ icon: 'error', title: 'Failed to save record' });
    }
  };

  const handleDelete = async (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.delete(`${backendUrl}/api/v360-license-info/${id}`, { headers: getHeaders() });
          Swal.fire('Deleted!', 'Record has been deleted.', 'success');
          fetchLicenseInfos(currentPage + 1);
        } catch (error) {
          console.error('Error deleting record', error);
          Swal.fire('Error!', 'Failed to delete record.', 'error');
        }
      }
    });
  };

  const parseValidDate = (dateStr) => {
      if (!dateStr) return null;
      const datePart = String(dateStr).split('T')[0];
      const parts = datePart.split('-');
      if (parts.length === 3) {
          if (parts[0].length === 4) {
              return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
          } else {
              return new Date(Number(parts[2]), Number(parts[1]) - 1, Number(parts[0]));
          }
      }
      const d = new Date(dateStr);
      return isNaN(d) ? null : d;
  };

  const getRowColorClass = (info) => {
    const validityDateStr = info.extended_validity || info.validity;
    if (!validityDateStr) return '';
    const validityDate = parseValidDate(validityDateStr);
    if (!validityDate) return '';
    validityDate.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const diffTime = validityDate - today;
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return 'text-danger fw-bold'; // Expired
    } else if (diffDays <= 30) {
      return 'text-warning fw-bold'; // Expiring in <= 30 days
    }
    return '';
  };

  const getLicenseStatus = (info) => {
      const validityDateStr = info.extended_validity || info.validity;
      if (!validityDateStr) return 'Active';
      const validityDate = parseValidDate(validityDateStr);
      if (!validityDate) return 'Active';
      validityDate.setHours(0, 0, 0, 0);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (validityDate < today) return 'Expired';
      if (info.extended_validity) return 'Extended';
      return 'Active';
  };

  const renderTableContent = () => (
    <>
      {(() => {
        let displayedLicenses = setupId 
            ? licenseInfos.filter(info => info.setup_id === setupId)
            : licenseInfos;
            
        if (autoOpenLicenseId) {
            displayedLicenses = displayedLicenses.filter(info => info.id === autoOpenLicenseId || info.license_id === autoOpenLicenseId);
        }
        
        return displayedLicenses.length > 0 ? (
          displayedLicenses.map((info, index) => {
            const rowClass = getRowColorClass(info);
            const displayValidity = info.extended_validity || info.validity;
            const activeStatus = getLicenseStatus(info);
            
            let badgeClass = 'bg-success';
            if (activeStatus === 'Expired') badgeClass = 'bg-danger';
            // Both Active and Extended use bg-success (green)

            return (
            <tr key={info.id}>
              <td className={rowClass}>{(currentPage * 15) + index + 1}</td>
              <td 
                className={`${rowClass} text-primary`}
                onClick={() => { 
                    setCurrentRecord(info); 
                    setShowDetailsModal(true); 
                }}
                style={{ cursor: 'pointer' }}
              >
                <div className="d-flex justify-content-center">
                  <div style={{ width: '150px', textAlign: 'left' }}>
                    {info.remark ? (
                      <span title={info.remark}>
                        {info.remark.length > 19 ? `${info.remark.substring(0, 19)}...` : info.remark}
                      </span>
                    ) : (
                      '-'
                    )}
                  </div>
                </div>
              </td>
              <td className={rowClass}>{info.tenant_id}</td>
            
              <td className={rowClass}>{info.start_date ? dayjs(info.start_date).format('DD-MM-YYYY') : ''}</td>
              <td className={rowClass}>{displayValidity ? dayjs(displayValidity).format('DD-MM-YYYY') : ''}</td>
              <td className={rowClass}>
                {(() => {
                  if (!displayValidity) return '-';
                  const validityDate = parseValidDate(displayValidity);
                  if (!validityDate) return '-';
                  validityDate.setHours(0, 0, 0, 0);
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  const diffTime = validityDate - today;
                  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
                  
                  if (diffDays < 0) return 'Expired';
                  if (diffDays === 0) return 'Expires Today';
                  return `${diffDays} Days`;
                })()}
              </td>
              <td>
                <span className={`badge ${activeStatus === 'Expired' ? 'bg-danger' : activeStatus === 'Extended' ? 'bg-soft-success text-success border border-success' : 'bg-success'}`}>
                  {String(activeStatus).toLowerCase()}
                </span>
              </td>
              <td>
                <div className="dropdown">
                  <i
                    className="mdi mdi-dots-horizontal font-size-18"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                    style={{ cursor: 'pointer' }}
                  ></i>
                  <ul className="dropdown-menu dropdown-menu-end font-size-14">
                    <li>
                      <button className="dropdown-item text-primary" onClick={() => { 
                          setCurrentRecord(info); 
                          setShowDetailsModal(true); 
                      }}>View Details</button>
                    </li>
                    <li>
                      <button className="dropdown-item text-success" onClick={() => { 
                          setCurrentRecord(info); 
                          setShowExtendModal(true); 
                      }}>Extend Date</button>
                    </li>
                    <li>
                      <button className="dropdown-item" onClick={() => handleShowModal(info)}>Edit</button>
                    </li>
                    <li>
                      <button className="dropdown-item text-danger" onClick={() => handleDelete(info.id)}>Delete</button>
                    </li>
                  </ul>
                </div>
              </td>
            </tr>
            );
          })
        ) : (
          <tr>
            <td colSpan="9" className="text-center">No License Info available</td>
          </tr>
        );
      })()}
    </>
  );

  const tableHeader = (
    <tr>
      <th className={isEmbedded ? "col" : "align-middle"}>Sl No</th>
      <th className={isEmbedded ? "col" : "align-middle"}>
        <div className="d-flex justify-content-center">
          <div style={{ width: '150px', textAlign: 'left' }}>
            Remark
          </div>
        </div>
      </th>
      <th className={isEmbedded ? "col" : "align-middle"}>Tenant ID</th>
      <th className={isEmbedded ? "col" : "align-middle"}>Start Date</th>
      <th className={isEmbedded ? "col" : "align-middle"}>Validity</th>
      <th className={isEmbedded ? "col" : "align-middle"}>Days Remaining</th>
     <th className={isEmbedded ? "col" : "align-middle"}>Status</th>

      <th className={isEmbedded ? "col" : "align-middle"}>Actions</th>
    </tr>
  );

  const paginator = pageCount > 1 && (
    <div className="d-flex justify-content-end mt-3">
      <ReactPaginate
        previousLabel={"<"}
        nextLabel={">"}
        breakLabel={"..."}
        pageCount={pageCount}
        marginPagesDisplayed={2}
        pageRangeDisplayed={3}
        onPageChange={(data) => fetchLicenseInfos(data.selected + 1)}
        forcePage={currentPage}
        containerClassName={"pagination pagination-rounded"}
        pageClassName={"page-item"}
        pageLinkClassName={"page-link"}
        previousClassName={"page-item"}
        previousLinkClassName={"page-link"}
        nextClassName={"page-item"}
        nextLinkClassName={"page-link"}
        breakClassName={"page-item"}
        breakLinkClassName={"page-link"}
        activeClassName={"active"}
      />
    </div>
  );

  const embeddedContent = (
    <div>
      <div className="d-flex justify-content-between mb-3 mt-2">
        <div className="d-flex">
          <div className="search-box me-2 mb-2 d-inline-block">
            <div className="position-relative">
              <input
                type="text"
                className="form-control"
                placeholder="Search license info..."
                value={searchQuery}
                onChange={handleSearchChange}
              />
              <i className="bx bx-search-alt search-icon"></i>
            </div>
          </div>
        </div>
        <div className="d-flex align-items-center gap-2">
          <Dropdown>
            <Dropdown.Toggle as={CustomToggle} />
            <Dropdown.Menu className="shadow-lg border-0 rounded-3">
              <Dropdown.Header className="fw-bold text-uppercase text-muted" style={{ fontSize: '10px', letterSpacing: '1px' }}>Filter by Expiry</Dropdown.Header>
              <Dropdown.Item active={expiryFilter === ''} onClick={() => handleExpiryFilterChange({target: {value: ''}})}>All Licenses</Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item active={expiryFilter === 'expired'} onClick={() => handleExpiryFilterChange({target: {value: 'expired'}})}>
                <span className="text-danger fw-bold"><i className="mdi mdi-alert-circle-outline me-1"></i>Expired</span>
              </Dropdown.Item>
              <Dropdown.Item active={expiryFilter === 'expired_7_days'} onClick={() => handleExpiryFilterChange({target: {value: 'expired_7_days'}})}>
                <span className="text-danger"><i className="mdi mdi-clock-alert-outline me-1"></i>Expired in 7 days</span>
              </Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item active={expiryFilter === '1_day'} onClick={() => handleExpiryFilterChange({target: {value: '1_day'}})}>
                <span className="text-warning"><i className="mdi mdi-timer-sand me-1"></i>Expiring in 1 Day</span>
              </Dropdown.Item>
              <Dropdown.Item active={expiryFilter === '7_days'} onClick={() => handleExpiryFilterChange({target: {value: '7_days'}})}>
                <span className="text-warning"><i className="mdi mdi-calendar-alert me-1"></i>Expiring in 7 Days</span>
              </Dropdown.Item>
              <Dropdown.Item active={expiryFilter === '30_days'} onClick={() => handleExpiryFilterChange({target: {value: '30_days'}})}>
                <span className="text-info"><i className="mdi mdi-calendar-clock me-1"></i>Expiring in 30 Days</span>
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
          <button
            type="button"
            className="btn rounded-circle border d-flex align-items-center justify-content-center round-create-button shadow-sm"
            onClick={() => handleShowModal()}
            data-bs-toggle="tooltip"
            data-bs-placement="top"
            title="Add License Info"
          >
            <i className="mdi mdi-plus" style={{ fontSize: "16px" }}></i>
          </button>
        </div>
      </div>
      <div className="contacts-table-modern">
        <div className="table-responsive" style={{ overflow: 'visible', minHeight: '200px', paddingBottom: '100px' }}>
          <table className="table table-modern">
            <thead>
              {tableHeader}
            </thead>
            <tbody>
              {renderTableContent()}
            </tbody>
          </table>
        </div>
        {paginator}
      </div>
    </div>
  );

  const innerContent = isEmbedded ? embeddedContent : (
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <div className="d-flex justify-content-between mb-3">
                    <div className="d-flex">
                      <div className="search-box me-2 mb-2 d-inline-block">
                        <div className="position-relative">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Search license info..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                          />
                          <i className="bx bx-search-alt search-icon"></i>
                        </div>
                      </div>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <Dropdown>
                        <Dropdown.Toggle as={CustomToggle} />
                        <Dropdown.Menu className="shadow-lg border-0 rounded-3">
                          <Dropdown.Header className="fw-bold text-uppercase text-muted" style={{ fontSize: '10px', letterSpacing: '1px' }}>Filter by Expiry</Dropdown.Header>
                          <Dropdown.Item active={expiryFilter === ''} onClick={() => handleExpiryFilterChange({target: {value: ''}})}>All Licenses</Dropdown.Item>
                          <Dropdown.Divider />
                          <Dropdown.Item active={expiryFilter === 'expired'} onClick={() => handleExpiryFilterChange({target: {value: 'expired'}})}>
                            <span className="text-danger fw-bold"><i className="mdi mdi-alert-circle-outline me-1"></i>Expired</span>
                          </Dropdown.Item>
                          <Dropdown.Item active={expiryFilter === 'expired_7_days'} onClick={() => handleExpiryFilterChange({target: {value: 'expired_7_days'}})}>
                            <span className="text-danger"><i className="mdi mdi-clock-alert-outline me-1"></i>Expired in 7 days</span>
                          </Dropdown.Item>
                          <Dropdown.Divider />
                          <Dropdown.Item active={expiryFilter === '1_day'} onClick={() => handleExpiryFilterChange({target: {value: '1_day'}})}>
                            <span className="text-warning"><i className="mdi mdi-timer-sand me-1"></i>Expiring in 1 Day</span>
                          </Dropdown.Item>
                          <Dropdown.Item active={expiryFilter === '7_days'} onClick={() => handleExpiryFilterChange({target: {value: '7_days'}})}>
                            <span className="text-warning"><i className="mdi mdi-calendar-alert me-1"></i>Expiring in 7 Days</span>
                          </Dropdown.Item>
                          <Dropdown.Item active={expiryFilter === '30_days'} onClick={() => handleExpiryFilterChange({target: {value: '30_days'}})}>
                            <span className="text-info"><i className="mdi mdi-calendar-clock me-1"></i>Expiring in 30 Days</span>
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                      <button
                        type="button"
                        className="btn rounded-circle border d-flex align-items-center justify-content-center round-create-button shadow-sm"
                        onClick={() => handleShowModal()}
                        data-bs-toggle="tooltip"
                        data-bs-placement="top"
                        title="Add License Info"
                      >
                        <i className="mdi mdi-plus"></i>
                      </button>
                    </div>
                  </div>
                  <div >
                    <table className="table align-middle table-nowrap table-check table-list">
                      <thead className="table-sales-gradient">
                        {tableHeader}
                      </thead>
                      <tbody>
                        {renderTableContent()}
                      </tbody>
                    </table>
                  </div>
                  {paginator}
                </div>
              </div>
            </div>
          </div>
  );

  const licenseModal = (
    <Offcanvas show={showModal} onHide={handleCloseModal} placement="end" style={{ width: isEmbedded ? '1180px' : '930px', zIndex: 1055 }} className="custom-offcanvas">
      <Offcanvas.Header closeButton>
        <Offcanvas.Title className="fw-bold text-primary">{currentRecord ? 'Edit License Info' : 'Add License Info'}</Offcanvas.Title>
      </Offcanvas.Header>
      <hr style={{ margin: 0 }} />
      <Offcanvas.Body>
        <Form onSubmit={handleSubmit}>
          <h5 className="mb-3 text-secondary">Basic Info</h5>
          <Row>
            {!isEmbedded && (
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label className="form-label form-font">Setup</Form.Label>
                  <Form.Select className="form-feild" name="setup_id" defaultValue={currentRecord?.setup_id || ''} required>
                    <option value="">Select Setup</option>
                    {setupInfos.map(setup => (
                      <option key={setup.id} value={setup.id}>{setup.client_name || setup.key}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            )}
            {isEmbedded && <input type="hidden" name="setup_id" value={currentRecord?.setup_id || setupId || ''} />}
            <Col md={isEmbedded ? 6 : 4}>
              <Form.Group className="mb-3">
                <Form.Label className="form-label form-font">Tenant ID</Form.Label>
                <Form.Control className="form-feild" type="number" name="tenant_id" defaultValue={currentRecord?.tenant_id || ''} />
              </Form.Group>
            </Col>
          </Row>

          <h5 className="mb-3 text-secondary mt-2">Status & Dates</h5>
          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label className="form-label form-font">Account Status</Form.Label>
                <Form.Select className="form-feild" name="acc_status" defaultValue={currentRecord?.acc_status ? String(currentRecord.acc_status).toLowerCase() : ''}>
                  <option value="">Select Status</option>
                  <option value="active">active</option>
                  <option value="deactivated">deactivated</option>
                  <option value="suspended">suspended</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label className="form-label form-font">Status Reason</Form.Label>
                <Form.Control className="form-feild" type="text" name="status_reason" defaultValue={currentRecord?.status_reason || ''} />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3 w-100">
                <Form.Label className="form-label form-font">Start Date</Form.Label>
                <DatePicker
                  selected={modalStartDate}
                  onChange={(date) => setModalStartDate(date)}
                  className="form-control form-feild w-100"
                  wrapperClassName="w-100"
                  dateFormat="dd-MM-yyyy"
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                  placeholderText="Select Start Date"
                  name="start_date"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3 w-100">
                <Form.Label className="form-label form-font">Validity</Form.Label>
                <DatePicker
                  selected={modalValidityDate}
                  onChange={(date) => setModalValidityDate(date)}
                  className="form-control form-feild w-100"
                  wrapperClassName="w-100"
                  dateFormat="dd-MM-yyyy"
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                  placeholderText="Select Validity Date"
                  name="validity"
                />
              </Form.Group>
            </Col>
          </Row>

          <h5 className="mb-3 text-secondary mt-2">Roles & Users</h5>
          <Row>
            <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">User Login</Form.Label><Form.Control className="form-feild" type="number" name="user_login" defaultValue={currentRecord?.user_login || ''} /></Form.Group></Col>
            <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">User Create</Form.Label><Form.Control className="form-feild" type="number" name="user_create" defaultValue={currentRecord?.user_create ?? ''} /></Form.Group></Col>
            <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">Super Admin</Form.Label><Form.Control className="form-feild" type="number" name="role_s_admin" defaultValue={currentRecord?.role_s_admin ?? ''} /></Form.Group></Col>
            <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">Admin</Form.Label><Form.Control className="form-feild" type="number" name="role_admin" defaultValue={currentRecord?.role_admin ?? ''} /></Form.Group></Col>
            <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">Supervisor</Form.Label><Form.Control className="form-feild" type="number" name="role_supervisor" defaultValue={currentRecord?.role_supervisor ?? ''} /></Form.Group></Col>
            <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">Teamlead</Form.Label><Form.Control className="form-feild" type="number" name="role_teamlead" defaultValue={currentRecord?.role_teamlead ?? ''} /></Form.Group></Col>
            <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">Analyst</Form.Label><Form.Control className="form-feild" type="number" name="role_analyst" defaultValue={currentRecord?.role_analyst ?? ''} /></Form.Group></Col>
            <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">C. Agent</Form.Label><Form.Control className="form-feild" type="number" name="role_cagent" defaultValue={currentRecord?.role_cagent ?? ''} /></Form.Group></Col>
            <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">V. Agent</Form.Label><Form.Control className="form-feild" type="number" name="role_vagent" defaultValue={currentRecord?.role_vagent ?? ''} /></Form.Group></Col>
            <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">T. Agent</Form.Label><Form.Control className="form-feild" type="number" name="role_tagent" defaultValue={currentRecord?.role_tagent ?? ''} /></Form.Group></Col>
            <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">Agent</Form.Label><Form.Control className="form-feild" type="number" name="role_agent" defaultValue={currentRecord?.role_agent ?? ''} /></Form.Group></Col>
            <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">Agent Plus</Form.Label><Form.Control className="form-feild" type="number" name="role_agent_plus" defaultValue={currentRecord?.role_agent_plus ?? ''} /></Form.Group></Col>
          </Row>

          <h5 className="mb-3 text-secondary mt-2">Modules & Features</h5>
          <Row>
            <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">Email</Form.Label><Form.Control className="form-feild" type="number" name="module_email" defaultValue={currentRecord?.module_email ?? ''} /></Form.Group></Col>
            <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">Voicemail</Form.Label><Form.Control className="form-feild" type="number" name="module_voicemail" defaultValue={currentRecord?.module_voicemail ?? ''} /></Form.Group></Col>
            <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">Chat</Form.Label><Form.Control className="form-feild" type="number" name="module_chat" defaultValue={currentRecord?.module_chat ?? ''} /></Form.Group></Col>
            <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">Interaction</Form.Label><Form.Control className="form-feild" type="number" name="module_interaction" defaultValue={currentRecord?.module_interaction ?? ''} /></Form.Group></Col>
            <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">Voice</Form.Label><Form.Control className="form-feild" type="number" name="module_voice" defaultValue={currentRecord?.module_voice ?? ''} /></Form.Group></Col>
            <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">Video</Form.Label><Form.Control className="form-feild" type="number" name="module_video" defaultValue={currentRecord?.module_video ?? ''} /></Form.Group></Col>
            <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">API</Form.Label><Form.Control className="form-feild" type="number" name="module_api" defaultValue={currentRecord?.module_api ?? ''} /></Form.Group></Col>
            <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">Ticket</Form.Label><Form.Control className="form-feild" type="number" name="module_ticket" defaultValue={currentRecord?.module_ticket ?? ''} /></Form.Group></Col>
            <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">Camp Voice</Form.Label><Form.Control className="form-feild" type="number" name="camp_voice" defaultValue={currentRecord?.camp_voice ?? ''} /></Form.Group></Col>
            <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">Camp Waba</Form.Label><Form.Control className="form-feild" type="number" name="camp_waba" defaultValue={currentRecord?.camp_waba ?? ''} /></Form.Group></Col>
            <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">Camp SMS</Form.Label><Form.Control className="form-feild" type="number" name="camp_sms" defaultValue={currentRecord?.camp_sms ?? ''} /></Form.Group></Col>
            <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">Camp Email</Form.Label><Form.Control className="form-feild" type="number" name="camp_email" defaultValue={currentRecord?.camp_email ?? ''} /></Form.Group></Col>
            <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">Mobile App</Form.Label><Form.Control className="form-feild" type="number" name="mobile_app" defaultValue={currentRecord?.mobile_app ?? ''} /></Form.Group></Col>
            <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">Wallboard</Form.Label><Form.Control className="form-feild" type="number" name="wallboard" defaultValue={currentRecord?.wallboard ?? ''} /></Form.Group></Col>
            <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">Gen AI</Form.Label><Form.Control className="form-feild" type="number" name="gen_ai" defaultValue={currentRecord?.gen_ai ?? ''} /></Form.Group></Col>
            <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">Toolbar App</Form.Label><Form.Control className="form-feild" type="number" name="toolbar_app" defaultValue={currentRecord?.toolbar_app ?? ''} /></Form.Group></Col>
            <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">AI Assist</Form.Label><Form.Control className="form-feild" type="number" name="ai_assist" defaultValue={currentRecord?.ai_assist ?? ''} /></Form.Group></Col>
            <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">AI Pro</Form.Label><Form.Control className="form-feild" type="number" name="ai_assist_pro" defaultValue={currentRecord?.ai_assist_pro ?? ''} /></Form.Group></Col>
          </Row>

          <h5 className="mb-3 text-secondary mt-2">IP & Remarks</h5>
          <Row>
            <Col md={4}>
              <Form.Group className="mb-3 mt-4">
                <Form.Check  type="checkbox" label={<span className="form-label form-font">Enable IP Check</span>} name="ip_check" defaultChecked={currentRecord?.ip_check || false} />
              </Form.Group>
            </Col>
            <Col md={8}>
              <Form.Group className="mb-3">
                <Form.Label className="form-label form-font">IP List </Form.Label>
                <Form.Control className="form-feild" type="text" name="ips" defaultValue={currentRecord?.ips ? currentRecord.ips.join(', ') : ''} />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="form-label form-font">Remark</Form.Label>
                <Form.Control className="form-feild" as="textarea" rows={2} name="remark" defaultValue={currentRecord?.remark || ''} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="form-label form-font">Remarks (Additional)</Form.Label>
                <Form.Control className="form-feild" as="textarea" rows={2} name="remarks" defaultValue={currentRecord?.remarks || ''} />
              </Form.Group>
            </Col>
          </Row>

          <div className="d-flex justify-content-end mt-4 mb-4">
            <Button variant="secondary" onClick={handleCloseModal} className="me-2">Cancel</Button>
            <Button variant="primary" type="submit">Save</Button>
          </div>
        </Form>
      </Offcanvas.Body>
    </Offcanvas>
  );

  const extraModals = (
    <>
      {showDetailsModal && currentRecord && (
        <LicenseInfoDetails
          show={showDetailsModal}
          onHide={() => setShowDetailsModal(false)}
          licenseId={currentRecord.id}
          onEditLicense={(license) => { setShowDetailsModal(false); handleShowModal(license); }}
        />
      )}

      <Offcanvas show={showExtendModal} onHide={() => setShowExtendModal(false)} placement="end" style={{ width: '500px', zIndex: 1060 }}>
        <Offcanvas.Header closeButton className="border-bottom">
          <Offcanvas.Title>Extend License Date</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <Form onSubmit={handleExtendSubmit}>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Extension Value</Form.Label>
                  <Form.Control 
                    type="number" 
                    min="1"
                    required
                    value={extendData.value}
                    onChange={e => setExtendData({...extendData, value: e.target.value})}
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Extension Type</Form.Label>
                  <Form.Select 
                    value={extendData.type}
                    onChange={e => setExtendData({...extendData, type: e.target.value})}
                  >
                    <option value="days">Days</option>
                    <option value="weeks">Weeks</option>
                    <option value="months">Months</option>
                    <option value="years">Years</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <div className="d-flex justify-content-end mt-4">
              <Button variant="secondary" onClick={() => setShowExtendModal(false)} className="me-2">Cancel</Button>
              <Button variant="success" type="submit">Extend License</Button>
            </div>
          </Form>
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );

  if (isEmbedded) {
    return (
      <>
        {innerContent}
        {licenseModal}
        {extraModals}
      </>
    );
  }

  return (
    <div className="main-content">
      <div className="page-content">
        <div className="container-fluid">
          <div className="row w-100">
            <div className="col-12">
              <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 className="mb-sm-0 font-size-18">V360 License Info</h4>
              </div>
            </div>
          </div>
          {innerContent}
          {licenseModal}
          {extraModals}
        </div>
      </div>
    </div>
  );
};

export default LicenseInfoList;
